import React, {useId, useRef, useState} from "react"
import {useClickAway, useToggle} from "react-use"
import {concat, noop, without} from "lodash"
import classNames from "classnames"
import SearchBox from "../SearchBox/SearchBox"
import VirtualizedList from "../VirtualizedList/VirtualizedList"
import {isUndefined} from "lodash"
import {OutlinedFilter} from "./Themes/OutlinedFilter"
import {SimpleFilter} from "./Themes/SimpleFilter"
import {IcomoonIconName} from "../Icon/icons"

export type CommonFilterProps<K> = {
	dropdownClassname: string
	filterRef: React.RefObject<HTMLInputElement>
	toggleDropdown: (nextValue?: any) => void
	showFilterSummary: boolean
	title?: string
	statuses: K[]
	toggleAll: typeof noop
	toggleNone: typeof noop
	isSearchable: boolean
	searchBox: JSX.Element
	buttons: JSX.Element[]
	customFilters?: {name: string; filterFn: () => void}[]
	buttonsLength: number
	singleSelect?: boolean
}
export function Filter<K>({
	options,
	statuses,
	setStatuses,
	title = "Filter",
	onFilterFunc,
	isSearchable = false,
	simpleTheme = false,
	filterIcon,
	customSelections,
	virtualized = false,
	singleSelect = false,
}: {
	options: Map<K, string>
	statuses: K[]
	setStatuses: (statuses: K[]) => void
	title?: string
	onFilterFunc?: typeof noop
	isSearchable?: boolean
	simpleTheme?: boolean
	filterIcon?: IcomoonIconName
	customSelections?: {name: string; filterFn: (option: [K, string]) => boolean}[]
	virtualized?: boolean
	/** Only one option can be selected at a time; selecting the active option clears it. */
	singleSelect?: boolean
}) {
	const all = options
	const ref = useRef(null)
	const radioGroupName = useId()
	const [isDropdownActive, toggleDropdown] = useToggle(false)
	const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined)
	const [searchBox] = SearchBox("", "Search", 0, onSearch, true)
	const showFilterSummary = statuses.length > 0 && (singleSelect || statuses.length != options.size)

	useClickAway(ref, () => toggleDropdown(false))
	const dropdownClassname = classNames("dropdown", isDropdownActive && "is-active")

	function toggle(status: K) {
		if (singleSelect) {
			setStatuses(statuses.includes(status) ? [] : [status])
			toggleDropdown(false)
		} else if (statuses.includes(status)) setStatuses(without(statuses, status))
		else if (!statuses.includes(status)) setStatuses(concat(statuses, status))
	}

	function toggleAll() {
		setStatuses(Array.from(all.keys()))
		toggleDropdown()
	}

	function toggleNone() {
		setStatuses([])
	}

	const customFilters = customSelections?.map((customSelection) => {
		return {
			name: customSelection.name,
			filterFn: () => {
				const arFromAll = Array.from(all)
				const filteredStatuses = arFromAll.filter(([option, value]) => customSelection.filterFn([option, value]))
				setStatuses(filteredStatuses.map(([option]) => option))
			},
		}
	})

	function onSearch(searchTerm: string) {
		if (searchTerm === "") {
			setSearchTerm(undefined)
		} else {
			setSearchTerm(searchTerm)
		}
	}

	const filteredItems = Array.from(all.entries()).filter(([, value]) => {
		if (isSearchable) {
			if (!isUndefined(searchTerm)) {
				if (!value.toLowerCase().includes(searchTerm.toLowerCase())) {
					return false
				}
			}
		}
		return true
	})

	const renderButtonRow = (key: K, value: string, ref?: (el: HTMLLabelElement | null) => void) => {
		return (
			<label ref={ref} key={value} className="panel-block no-border-bottom wide-panel simple-checkbox">
				<input
					type={singleSelect ? "radio" : "checkbox"}
					name={singleSelect ? radioGroupName : undefined}
					onClick={
						singleSelect && statuses.includes(key)
							? () => {
									toggle(key)
									onFilterFunc ? onFilterFunc() : null
							  }
							: undefined
					}
					onChange={(_) => {
						if (singleSelect && statuses.includes(key)) return
						toggle(key)
						onFilterFunc ? onFilterFunc() : null
					}}
					checked={statuses.includes(key)}
				/>
				{value}
			</label>
		)
	}

	const buttons = virtualized
		? [<VirtualizedList key="filter-list" items={filteredItems} renderRow={renderButtonRow} />]
		: filteredItems.map(([key, value]: [key: K, value: string]) => renderButtonRow(key, value))

	const commonFilterProps = {
		dropdownClassname,
		filterRef: ref,
		toggleDropdown,
		showFilterSummary,
		title,
		statuses,
		toggleAll,
		toggleNone,
		isSearchable,
		searchBox,
		toggle,
		onFilterFunc,
		buttons,
		customFilters,
		buttonsLength: filteredItems.length,
		singleSelect,
	}

	return simpleTheme ? (
		<SimpleFilter {...commonFilterProps} filterIcon={filterIcon} />
	) : (
		<OutlinedFilter {...commonFilterProps} />
	)
}
